import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { speciesIndex } from "../sim/species";
import type { Terrain } from "./terrain";
import type { SimClient } from "../worker/client";

import "@babylonjs/core/Meshes/thinInstanceMesh";

/**
 * One instanced boids swarm of pollinators that prefers dense flowering heather
 * (retargeting toward live heather each so often) and otherwise wanders the
 * island. Cheap, and it does more emotional "alive" work than another 10k
 * triangles. Ties visually to the Phase 1 pollination model (heather seed-set
 * rises with local heather density).
 */
const COUNT = 64;
const HEATHER = speciesIndex("heather");
const MAX_SPEED = 3.5;
const HOVER = 0.6; // metres above ground

export class PollinatorSwarm {
  private readonly mesh: Mesh;
  private readonly matrices = new Float32Array(16 * COUNT);
  private readonly px = new Float32Array(COUNT);
  private readonly py = new Float32Array(COUNT);
  private readonly pz = new Float32Array(COUNT);
  private readonly vx = new Float32Array(COUNT);
  private readonly vz = new Float32Array(COUNT);
  private readonly engine: { getDeltaTime(): number };
  private tx = 0;
  private tz = 0;
  private retarget = 0;

  constructor(
    scene: Scene,
    private readonly terrain: Terrain,
    private readonly sim: SimClient,
  ) {
    this.engine = scene.getEngine();
    this.mesh = CreateSphere("pollinator", { diameter: 0.07, segments: 4 }, scene);
    const mat = new StandardMaterial("pollinatorMat", scene);
    mat.emissiveColor = new Color3(1.0, 0.92, 0.5); // bright specks catch the eye
    mat.disableLighting = true;
    this.mesh.material = mat;
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true;

    const half = terrain.grid.worldSize * 0.4;
    for (let i = 0; i < COUNT; i++) {
      this.px[i] = (Math.random() - 0.5) * half;
      this.pz[i] = (Math.random() - 0.5) * half;
      this.py[i] = terrain.heightAt(this.px[i]!, this.pz[i]!) + HOVER;
    }
    // Allocate the instance matrix buffer ONCE (updatable); per frame we mutate
    // `matrices` in place and re-upload via thinInstanceBufferUpdated. Calling
    // thinInstanceSetBuffer every frame churns the GPU buffer (ref-counted by its
    // 4 world-matrix vertex buffers, so the old one isn't freed) — a slow leak
    // that ends in WebGL context loss. Same rule as GardenRenderer.
    this.mesh.thinInstanceSetBuffer("matrix", this.matrices, 16, false);
    this.mesh.thinInstanceCount = COUNT;
    scene.onBeforeRenderObservable.add(() => this.update());
  }

  private update(): void {
    const dt = Math.min(0.05, this.engine.getDeltaTime() / 1000);
    this.pickTarget(dt);

    const m = new Matrix();
    for (let i = 0; i < COUNT; i++) {
      // Boids: cohesion + separation + alignment over the swarm, plus seek target.
      let cx = 0, cz = 0, sx = 0, sz = 0, ax = 0, az = 0, near = 0;
      for (let j = 0; j < COUNT; j++) {
        if (j === i) continue;
        const dx = this.px[j]! - this.px[i]!;
        const dz = this.pz[j]! - this.pz[i]!;
        const d2 = dx * dx + dz * dz;
        if (d2 > 9) continue;
        near++;
        cx += this.px[j]!;
        cz += this.pz[j]!;
        ax += this.vx[j]!;
        az += this.vz[j]!;
        if (d2 < 0.35) {
          sx -= dx;
          sz -= dz;
        }
      }
      let fx = 0, fz = 0;
      if (near > 0) {
        fx += (cx / near - this.px[i]!) * 0.6 + ax / near * 0.4 + sx * 2.2;
        fz += (cz / near - this.pz[i]!) * 0.6 + az / near * 0.4 + sz * 2.2;
      }
      // seek target (heather patch or wander point)
      fx += (this.tx - this.px[i]!) * 0.5;
      fz += (this.tz - this.pz[i]!) * 0.5;
      // gentle per-bug jitter for liveliness
      fx += (Math.random() - 0.5) * 1.5;
      fz += (Math.random() - 0.5) * 1.5;

      this.vx[i]! += fx * dt;
      this.vz[i]! += fz * dt;
      const sp = Math.hypot(this.vx[i]!, this.vz[i]!);
      if (sp > MAX_SPEED) {
        this.vx[i]! *= MAX_SPEED / sp;
        this.vz[i]! *= MAX_SPEED / sp;
      }
      this.px[i]! += this.vx[i]! * dt;
      this.pz[i]! += this.vz[i]! * dt;
      // bob just above the ground
      const ground = this.terrain.heightAt(this.px[i]!, this.pz[i]!);
      this.py[i]! = ground + HOVER + Math.sin(performance.now() * 0.004 + i) * 0.15;

      Matrix.TranslationToRef(this.px[i]!, this.py[i]!, this.pz[i]!, m);
      m.copyToArray(this.matrices, i * 16);
    }
    this.mesh.thinInstanceBufferUpdated("matrix"); // re-upload in place; buffer allocated in ctor
  }

  /** Drift the swarm target toward a live heather plant; else wander. */
  private pickTarget(dt: number): void {
    this.retarget -= dt;
    if (this.retarget > 0) return;
    this.retarget = 2 + Math.random() * 3;
    const cur = this.sim.cur;
    const heathers: number[] = [];
    if (cur) {
      for (let i = 0; i < cur.count; i++) {
        if (cur.alive[i] === 1 && cur.species[i] === HEATHER) heathers.push(i);
      }
    }
    if (heathers.length > 0) {
      const i = heathers[(Math.random() * heathers.length) | 0]!;
      this.tx = cur!.x[i]!;
      this.tz = cur!.z[i]!;
    } else {
      const half = this.terrain.grid.worldSize * 0.35;
      this.tx = (Math.random() - 0.5) * 2 * half;
      this.tz = (Math.random() - 0.5) * 2 * half;
    }
  }
}
